import {FluxAvatar} from "@buildwithflux/avatar-react-mui";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import DocumentThumb from "../common/DocumentThumb";
import {FluxExpandCollapse} from "../common/FluxExpandCollapse";
import MomentWithTooltip from "../common/MomentWithTooltip";

import {CommentCounter, StarCounter, UsageCounter} from "./Counter";
import StarButton from "./StarButton";

const AVATAR_SIZE = 20;

export interface ProjectHit {
    uid: string;
    name: string;
    description?: string;
    url: string;
    thumbUrl?: string;
    ownerUid: string;
    ownerHandle: string;
    contributorUids: string[];
    usageCount?: number;
    commentCount?: number;
    starCount?: number;
    updatedAt?: number;
    ownerVerified?: boolean;
}

function lastModifiedTooltip(formattedDate: string) {
    return `Last modified: ${formattedDate}`;
}

export default function ProjectCard({project}: {project: ProjectHit}) {
    // Owner is rendered as the leading avatar already, so drop it from the extras.
    const contributorUids = project.contributorUids.filter((uid) => uid && uid !== project.ownerUid);

    return (
        <>
            <Paper square component="li" sx={{p: 2}}>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "flex-start",
                        alignItems: "center",
                    }}
                >
                    <Box sx={{paddingRight: "16px", flexGrow: 0.5}}>
                        <DocumentThumb thumbUrl={project.thumbUrl} documentName={project.name} size={64} />
                    </Box>

                    <Box sx={{width: "360px", flexGrow: 11}}>
                        <Link href={project.url} color="textPrimary">
                            <Typography color="textPrimary" variant="h6" component="h3" sx={{mb: 0}}>
                                {project.name}
                            </Typography>
                        </Link>

                        <Typography color="textPrimary" variant="body2" component="h3" sx={{mb: 0, mr: 1}}>
                            <FluxExpandCollapse
                                previewHeight={
                                    40 // three lines at body2
                                }
                            >
                                {project.description ?? ""}
                            </FluxExpandCollapse>
                        </Typography>

                        <Box sx={{marginTop: "5px", display: "flex", gap: 1, alignItems: "center"}}>
                            <Box
                                sx={{
                                    display: "inline-block",
                                    width: "20px", // So it doesn't resize after the image loads
                                    verticalAlign: "-5px", // To align with text baseline
                                }}
                            >
                                <Link href={`/${project.ownerHandle}`} aria-label={project.ownerHandle}>
                                    <FluxAvatar uid={project.ownerUid} size={AVATAR_SIZE} alt={project.ownerHandle} />
                                </Link>
                            </Box>
                            <Box sx={{display: "inline"}}>
                                <Typography variant="body2" noWrap sx={{display: "inline"}}>
                                    <Tooltip title={project.ownerHandle} placement="top">
                                        <Link
                                            href={`/${project.ownerHandle}`}
                                            sx={{
                                                wordWrap: "break-word",
                                                hyphens: "none",
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                                whiteSpace: "nowrap",
                                                color: "primary.main",
                                            }}
                                        >
                                            {project.ownerHandle}
                                        </Link>
                                    </Tooltip>
                                </Typography>
                            </Box>
                            {project.ownerVerified ? (
                                <CheckCircleIcon sx={{fontSize: 12, flexShrink: 0, color: "primary.main"}} />
                            ) : null}
                            {contributorUids.length > 0 && (
                                <>
                                    <Typography variant="body2" sx={{display: "inline"}} color="textSecondary">
                                        +
                                    </Typography>
                                    {contributorUids.map((uid) => (
                                        <Box
                                            key={uid}
                                            sx={{
                                                display: "inline-block",
                                                width: "20px",
                                                verticalAlign: "-5px",
                                            }}
                                        >
                                            <FluxAvatar uid={uid} size={AVATAR_SIZE} />
                                        </Box>
                                    ))}
                                </>
                            )}
                            {project.updatedAt ? (
                                <Typography variant="body2" color="textSecondary" sx={{display: "inline"}}>
                                    <MomentWithTooltip
                                        date={project.updatedAt}
                                        customTooltipRenderer={lastModifiedTooltip}
                                    />
                                </Typography>
                            ) : null}
                        </Box>

                        <Box sx={{marginTop: "2px", display: "flex", gap: 1.25}}>
                            <UsageCounter count={project.usageCount} />
                            <CommentCounter count={project.commentCount} />
                            <StarCounter count={project.starCount} />
                        </Box>
                    </Box>

                    <Box sx={{flexGrow: 2}}>
                        <StarButton documentUid={project.uid} />
                    </Box>
                </Box>
            </Paper>
            <Divider />
        </>
    );
}
